import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { fetchReports, fetchAuditTrail, verifyAuditChain, generateReport } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, FileText, CheckCircle, XCircle, Download, RefreshCw, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const FRAMEWORKS = [
  { id: 'SOC2', label: 'SOC 2 Type II', desc: 'Control Evidence Report', color: '#06B6D4' },
  { id: 'ISO20000', label: 'ISO 20000-1', desc: 'ITSM Process Audit', color: '#3B82F6' },
  { id: 'SOX', label: 'SOX', desc: 'IT General Controls', color: '#F59E0B' },
  { id: 'HIPAA', label: 'HIPAA', desc: 'Access & Audit Log', color: '#EF4444' },
  { id: 'GDPR', label: 'GDPR', desc: 'Data Processing Activity', color: '#10B981' },
];

export default function CompliancePage() {
  const [verifyResult, setVerifyResult] = useState(null);

  const { data: reports } = useQuery({ queryKey: ['reports'], queryFn: fetchReports });
  const { data: auditEntries } = useQuery({ queryKey: ['audit-trail'], queryFn: () => fetchAuditTrail(50) });

  const generateMutation = useMutation({
    mutationFn: (framework) => generateReport(framework),
    onSuccess: (data) => toast.success(`${data.framework} report generated`, { description: `Report ID: ${data.id?.slice(0,8)}` }),
    onError: () => toast.error('Failed to generate report'),
  });

  const verifyMutation = useMutation({
    mutationFn: () => verifyAuditChain(),
    onSuccess: (data) => { setVerifyResult(data); toast.success(data.valid ? 'Hash chain verified ✔' : 'Chain integrity issue detected'); },
    onError: () => toast.error('Verification failed'),
  });

  return (
    <div data-testid="compliance-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Compliance & Audit</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">COMPLIANCE-OPS: immutable audit trail, regulatory reports, policy enforcement</p>
      </div>

      {/* Report Generation */}
      <div className="nexus-card relative overflow-hidden mb-4">
        <div className="p-3 border-b border-[var(--nexus-border)]">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Generate Compliance Report</h2>
        </div>
        <div className="p-3 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="report-frameworks">
          {FRAMEWORKS.map(fw => (
            <button key={fw.id} onClick={() => generateMutation.mutate(fw.id)} disabled={generateMutation.isPending}
              className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.04] transition-colors text-left"
              data-testid={`generate-${fw.id.toLowerCase()}`}>
              <span className="text-xs font-semibold" style={{ color: fw.color }}>{fw.label}</span>
              <p className="text-[10px] text-[var(--nexus-text-muted)] mt-0.5">{fw.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Hash Chain Verification */}
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link2 size={14} className="text-[var(--nexus-accent)]" />
                <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Audit Trail Hash Chain</h2>
              </div>
              <button onClick={() => verifyMutation.mutate()} disabled={verifyMutation.isPending}
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-[var(--nexus-accent)] bg-opacity-10 text-[var(--nexus-accent)] text-[10px] hover:bg-opacity-20 disabled:opacity-50"
                data-testid="verify-chain">
                <RefreshCw size={10} className={verifyMutation.isPending ? 'animate-spin' : ''} /> Verify Integrity
              </button>
            </div>
          </div>
          <div className="p-4">
            {verifyResult ? (
              <div className="space-y-3">
                <div className={`flex items-center gap-3 p-3 rounded-lg ${verifyResult.valid ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                  {verifyResult.valid ? <CheckCircle size={20} className="text-[var(--nexus-success)]" /> : <XCircle size={20} className="text-[var(--nexus-danger)]" />}
                  <div>
                    <p className={`text-sm font-semibold ${verifyResult.valid ? 'text-[var(--nexus-success)]' : 'text-[var(--nexus-danger)]'}`}>
                      {verifyResult.valid ? 'Chain Integrity Verified' : 'Integrity Issue Detected'}
                    </p>
                    <p className="text-[10px] text-[var(--nexus-text-muted)]">{verifyResult.entries_checked} entries checked, {verifyResult.errors?.length || 0} errors</p>
                  </div>
                </div>
                {verifyResult.errors?.length > 0 && verifyResult.errors.map((e, i) => (
                  <div key={i} className="p-2 rounded bg-red-500/5 border border-red-500/10 text-[10px] text-red-300 font-mono">Seq #{e.sequence}: {e.error}</div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[var(--nexus-text-muted)] text-center py-4">Click "Verify Integrity" to validate the SHA-256 hash chain</p>
            )}
          </div>
        </div>

        {/* Generated Reports */}
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-[var(--nexus-success)]" />
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Generated Reports</h2>
            </div>
          </div>
          <ScrollArea className="h-[300px]">
            <div className="p-2 space-y-1.5">
              {(reports || []).map(r => (
                <div key={r.id} className="p-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold text-[var(--nexus-text-primary)]">{r.title || r.framework}</span>
                      <p className="text-[10px] text-[var(--nexus-text-muted)] mt-0.5">Period: {r.period} | Status: {r.status}</p>
                    </div>
                    <span className="font-mono text-[10px] text-[var(--nexus-success)]">{r.status}</span>
                  </div>
                  {r.summary && (
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[var(--nexus-text-muted)]">
                      <span>{r.summary.audit_entries} audit entries</span>
                      <span>{r.summary.total_tickets} tickets</span>
                      <span>{r.summary.agent_actions} agent actions</span>
                    </div>
                  )}
                </div>
              ))}
              {(!reports || reports.length === 0) && <div className="text-center py-6 text-[var(--nexus-text-muted)] text-xs">No reports generated yet. Click a framework above.</div>}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Immutable Audit Trail */}
      <div className="nexus-card relative overflow-hidden mt-4">
        <div className="p-3 border-b border-[var(--nexus-border)]">
          <div className="flex items-center gap-2">
            <Shield size={14} className="text-[var(--nexus-warning)]" />
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Immutable Audit Trail (SHA-256 Hash Chain)</h2>
          </div>
        </div>
        <ScrollArea className="h-[250px]">
          <div className="p-2 space-y-1" data-testid="immutable-audit">
            {(auditEntries || []).map(e => (
              <div key={e.id} className="flex items-start gap-2 p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                <span className="font-mono text-[9px] text-[var(--nexus-accent)] tabular-nums w-8 flex-shrink-0">#{e.sequence_number}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-secondary)] border border-[var(--nexus-border)] font-mono">{e.action}</span>
                    <span className="text-[10px] text-[var(--nexus-accent)]">{e.actor_type}:{e.actor_id}</span>
                    <span className="text-[10px] text-[var(--nexus-text-muted)]">{e.entity_type} {e.entity_id}</span>
                  </div>
                  <span className="font-mono text-[8px] text-[var(--nexus-text-muted)] truncate block">{e.current_hash?.slice(0, 32)}...</span>
                </div>
                <span className="font-mono text-[9px] text-[var(--nexus-text-muted)] flex-shrink-0">{e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : ''}</span>
              </div>
            ))}
            {(!auditEntries || auditEntries.length === 0) && <div className="text-center py-6 text-[var(--nexus-text-muted)] text-xs">No audit entries yet. Trigger a scenario to generate entries.</div>}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
